import { ChevronDown, Plus } from 'lucide-react';

import type { Dashboard, FinanceSnapshot } from '../api/contract';
import { formatMoney } from '../lib/money';
import { FinanceBubble } from '../shared/ui/FinanceBubble';
import { MoneyText } from '../shared/ui/MoneyText';
import { getBudgetFinanceIconId } from '../shared/ui/financeAppearance';

export type DashboardDragPayload =
  | { kind: 'income'; categoryId: string }
  | { kind: 'budget'; budgetId: string };

export function isCompatibleDashboardDrop(
  payload: DashboardDragPayload | undefined,
  targetId: string,
): boolean {
  if (payload === undefined) return false;
  if (payload.kind === 'income') return targetId.startsWith('budget:');
  if (targetId.startsWith('expense:')) return true;
  return targetId.startsWith('budget:') && targetId !== `budget:${payload.budgetId}`;
}

const dashboardColumnCount = 5;

export function shouldUseCompactDashboardAdd(itemCount: number): boolean {
  return itemCount > 0 && itemCount % dashboardColumnCount === 0;
}

export function DashboardBudgetSection<Payload = DashboardDragPayload>({
  budgets,
  budgetById,
  collapsed,
  canManageStructure,
  dragPayload,
  onToggle,
  onDragStart,
  onDragCancel,
  onDrop,
  onContextAction,
  onCreate,
  onEdit,
  canDrop = isCompatibleDashboardDrop as (payload: Payload | undefined, targetId: string) => boolean,
  budgetPayload = (budgetId: string) => ({ kind: 'budget', budgetId }) as Payload,
}: {
  budgets: Dashboard['budgetSummaries'];
  budgetById: ReadonlyMap<string, FinanceSnapshot['budgets'][number]>;
  collapsed: boolean;
  canManageStructure: boolean;
  dragPayload?: Payload;
  onToggle: () => void;
  onDragStart: (payload: Payload) => void;
  onDragCancel: () => void;
  onDrop: (payload: Payload, targetId: string) => void;
  onContextAction: (payload: Payload) => void;
  onCreate: () => void;
  onEdit: (budgetId: string) => void;
  canDrop?: (payload: Payload | undefined, targetId: string) => boolean;
  budgetPayload?: (budgetId: string) => Payload;
}) {
  const budgetsTotal = budgets
    .filter(item => budgetById.get(item.budgetId)?.includeInTotal !== false)
    .reduce((sum, item) => sum + item.balance, 0);
  const useCompactBudgetAdd = shouldUseCompactDashboardAdd(budgets.length);

  return (
    <article className="card finance-section span-two">
      <header className="card-header finance-header">
        <button
          className="finance-section-toggle"
          aria-label={`${collapsed ? 'Развернуть' : 'Свернуть'} раздел Бюджеты`}
          onClick={onToggle}
        >
          <ChevronDown className={collapsed ? 'collapsed' : ''} size={18} />
          <h2>Бюджеты</h2>
        </button>
        <div className="finance-amount-row">
          <MoneyText
            fit
            minimumScale={0.75}
            className="finance-section-amount"
            currencyClassName="finance-section-currency"
            value={formatMoney(budgetsTotal)}
          />
          {canManageStructure && useCompactBudgetAdd && (
            <CompactFinanceAddButton
              label="Добавить бюджет"
              onClick={onCreate}
            />
          )}
        </div>
      </header>
      <div className="finance-bubbles" hidden={collapsed}>
        {budgets.map(item => {
          const budget = budgetById.get(item.budgetId);
          return (
            <FinanceBubble
              canDrop={canDrop}
              key={item.budgetId}
              name={budget?.name ?? 'Бюджет'}
              amount={item.balance}
              caption={budget?.goal ? formatMoney(budget.goal) : undefined}
              color={budget?.accent}
              icon={getBudgetFinanceIconId(budget?.name, budget?.icon)}
              progress={budget?.goal ? item.progress : undefined}
              mutedAmount={budget?.includeInTotal === false}
              dragPayload={budgetPayload(item.budgetId)}
              dropTargetId={`budget:${item.budgetId}`}
              dropTargetActive={canDrop(
                dragPayload,
                `budget:${item.budgetId}`,
              )}
              onDragStart={onDragStart}
              onDragCancel={onDragCancel}
              onDrop={onDrop}
              onContextAction={onContextAction}
              onClick={() => canManageStructure && onEdit(item.budgetId)}
            />
          );
        })}
        {canManageStructure && !useCompactBudgetAdd && (
          <button
            className="finance-add"
            aria-label="Добавить бюджет"
            onClick={onCreate}
          >
            <span className="finance-add-spacer" />
            <span className="finance-add-circle">
              <Plus size={26} strokeWidth={2.2} />
            </span>
          </button>
        )}
      </div>
    </article>
  );
}

export function CompactFinanceAddButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button className="finance-add-compact" aria-label={label} onClick={onClick}>
      <Plus size={17} strokeWidth={2.2} />
    </button>
  );
}
